import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { UserManager, WebStorageStateStore } from "oidc-client";
import oidcConfig from "../config/oidc-config";
import { ApplicationApi } from "../service/Api/ApplicationsApi";

const AUTH_CALLBACK_ROUTES = [
  "/auth-callback",
  "/auth-callback-logout",
  "/auth-callback-silent",
];

const AuthContext = createContext({
  user: null,
  setUser: () => {},
  userManager: null,
  blueButtonToken: null,
  setBlueButtonToken: () => {},
  CoveragePlanCode: null,
  setCoveragePlanCode: () => {},
  PatientIDHL7: null,
  setPatientIDHL7: () => {},
  setUserRole: () => {},
  userRole: null,
  isLoading: true,
  applyAuthenticatedUser: async () => {},
});

const persistAccessToken = (accessToken) => {
  localStorage.removeItem("PortalAdmin-AccessToken");
  if (accessToken) {
    localStorage.setItem("PortalAdmin-AccessToken", accessToken);
  }
};

export const AuthProvider = ({ children }) => {
  const initialCoveragePlanCode = localStorage.getItem("CoveragePlanCode");
  const initialPatientIDHL7 = localStorage.getItem("PatientIDHL7");
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState();
  const [blueButtonToken, setBlueButtonToken] = useState();
  const [CoveragePlanCode, setCoveragePlanCodeState] = useState(
    initialCoveragePlanCode
  );
  const [PatientIDHL7, setPatientIDHL7State] = useState(initialPatientIDHL7);
  // Start true so protected routes wait for session hydration before redirecting.
  const [isLoading, setIsLoading] = useState(true);

  const userManager = useMemo(
    () =>
      new UserManager({
        ...oidcConfig,
        userStore: new WebStorageStateStore({ store: window.localStorage }),
      }),
    []
  );

  const setCoveragePlanCode = (value) => {
    localStorage.setItem("CoveragePlanCode", value);
    setCoveragePlanCodeState(value);
  };

  const setPatientIDHL7 = (value) => {
    localStorage.setItem("PatientIDHL7", value);
    setPatientIDHL7State(value);
  };

  const applyAuthenticatedUser = useCallback(async (authenticatedUser) => {
    if (!authenticatedUser || authenticatedUser.expired) {
      setUser(null);
      return;
    }

    setUser(authenticatedUser);
    persistAccessToken(authenticatedUser.access_token);

    try {
      const ssoId = authenticatedUser.profile?.sub;
      if (ssoId) {
        const userResponse = await ApplicationApi.getUserBySSOID(ssoId);
        setUserRole(userResponse.roleID);
      }
    } catch (roleError) {
      console.error("Error loading user role:", roleError);
    }
  }, []);

  useEffect(() => {
    const initUser = async () => {
      // Callback routes establish the session themselves; avoid racing getUser().
      if (AUTH_CALLBACK_ROUTES.includes(window.location.pathname)) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const currentUser = await userManager.getUser();
        if (currentUser && !currentUser.expired) {
          await applyAuthenticatedUser(currentUser);
        } else {
          setUser(null);
        }
      } catch (e) {
        console.error("Error loading user:", e);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    initUser();
  }, [userManager, applyAuthenticatedUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        userManager,
        blueButtonToken,
        setBlueButtonToken,
        CoveragePlanCode,
        setCoveragePlanCode,
        PatientIDHL7,
        setPatientIDHL7,
        userRole,
        setUserRole,
        isLoading,
        setIsLoading,
        applyAuthenticatedUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
